import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { API, toast } from '@/App';
import { CheckCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { trackLogin, trackSignup, trackPricingView } from '@/utils/analytics';
import SEOHead, { seoConfigs, generateFAQSchema, courseSchema } from '@/components/SEOHead';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TestimonialsSection from '@/components/landing/TestimonialsSection';
import FeaturesSection from '@/components/landing/FeaturesSection';
import PricingSection from '@/components/landing/PricingSection';
import FAQSection from '@/components/landing/FAQSection';
import SignupBanner from '@/components/landing/SignupBanner';
import TrialRequestDialog from '@/components/landing/TrialRequestDialog';
import FloatingTrialBubble from '@/components/FloatingTrialBubble';

// Lazy load ONLY modals (loaded when user interacts)
const LazyLoginDialog = lazy(() => import('@/components/landing/AuthModals').then(m => ({ default: m.LoginDialog })));
const LazyForgotPasswordDialog = lazy(() => import('@/components/landing/AuthModals').then(m => ({ default: m.ForgotPasswordDialog })));
const LazyRegisterDialog = lazy(() => import('@/components/landing/AuthModals').then(m => ({ default: m.RegisterDialog })));

// FAQ data for structured data
const faqData = [
  {
    question: "How many full-length SAT practice tests are included?",
    answer: "Practice4SAT includes 9 full-length adaptive digital SAT practice tests (including a free diagnostic), totaling 4,500+ SAT practice questions with step-by-step explanations. Each test mirrors the official Digital SAT format with realistic timed practice and adaptive difficulty."
  },
  {
    question: "Is there a free SAT diagnostic test I can try?",
    answer: "Yes! We offer a completely free SAT diagnostic test that assesses your skills across both Reading & Writing and Math. Get instant score predictions and identify skill gaps—no account required. It's the best way to start your digital SAT preparation."
  },
  {
    question: "How does the adaptive SAT practice test work?",
    answer: "Our digital SAT simulator uses the same adaptive format as the official Digital SAT. Your performance on Module 1 determines whether you receive an easier or harder Module 2, just like the real exam. This provides the most realistic SAT practice test experience and accurate score estimates."
  },
  {
    question: "What SAT topics and practice questions are covered?",
    answer: "Our SAT question bank covers all 8 official Digital SAT domains: Algebra, Advanced Math, Geometry & Trigonometry, Problem Solving & Data Analysis, Information and Ideas, Craft and Structure, Expression of Ideas, and Standard English Conventions. Practice with SAT math questions, SAT reading practice questions, and SAT writing practice questions—all with detailed explanations."
  },
  {
    question: "Can I review my answers and get SAT score predictions?",
    answer: "Absolutely! After each SAT practice exam, you get comprehensive scoring analysis with performance breakdowns by topic. Our SAT score predictor uses calibrated difficulty to provide reliable score estimates, helping you track your SAT score improvement over time."
  },
  {
    question: "How can Practice4SAT help me improve my SAT score?",
    answer: "Practice4SAT provides a complete SAT preparation system: start with a free diagnostic, follow a personalized SAT study plan, practice with targeted SAT practice questions, and verify progress with full-length digital SAT mock tests. Our adaptive practice and detailed analytics are designed to accelerate score improvement."
  }
];

export default function LandingPage({ setUser }) {
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', promoCode: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoMessage, setPromoMessage] = useState('');
  const [showTrialRequest, setShowTrialRequest] = useState(false);

  // Check URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('showAuth') === 'true') {
      setShowAuth(true);
    }

    // Handle return from Stripe Checkout
    const paymentStatus = params.get('payment');
    const sessionId = params.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      toast.message('Verifying payment...');
      // Clean URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      pollPaymentStatus(sessionId);
      return;
    }
    if (paymentStatus === 'cancelled') {
      toast.error('Payment cancelled. You can try again anytime.');
      window.history.replaceState({}, '', window.location.pathname);
    }

    const shouldOpenRegister = sessionStorage.getItem('openRegisterModal');
    if (shouldOpenRegister === 'true') {
      sessionStorage.removeItem('openRegisterModal');
      sessionStorage.removeItem('selectedPackage');
      setShowRegister(true);
    }
    
    // Handle scroll to pricing from quiz results (via URL param or sessionStorage)
    const scrollToPricing = sessionStorage.getItem('scrollToPricing');
    const signupParam = params.get('signup');
    const hash = location.hash;
    
    if (scrollToPricing === 'true' || signupParam === 'true' || hash === '#pricing') {
      sessionStorage.removeItem('scrollToPricing');
      // Clean up URL
      if (signupParam || hash) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setShowAuth(false);
      trackLogin();
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success('Password reset link sent to your email.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    }
    setLoading(false);
  };

  const validatePromoCode = async (code) => {
    if (!code || code.length < 3) {
      setPromoValid(null);
      setPromoMessage('');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/validate-promo`, { code });
      if (response.data.valid) {
        setPromoValid(true);
        setPromoMessage(`✓ Promo code valid: ${response.data.discount_type === 'free' ? 'FREE access!' : `${response.data.discount_percent}% off`}`);
      } else {
        setPromoValid(false);
        setPromoMessage('Invalid promo code');
      }
    } catch (error) {
      setPromoValid(false);
      setPromoMessage('Invalid promo code');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        package_id: 'full',
        origin_url: window.location.origin,
        user_email: formData.email,
        user_name: formData.name,
        user_password: formData.password,
      });

      // Persist email so we can pre-fill login after successful payment
      sessionStorage.setItem('postPayEmail', formData.email);

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start checkout');
      setLoading(false);
    }
  };

  // Poll Stripe payment status after redirect back from Checkout
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 2000;
    if (attempts >= MAX_ATTEMPTS) {
      toast.error('Payment status check timed out. Please check your email or try logging in.');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === 'paid' && data.user_created) {
        trackSignup('paid');
        toast.success('Payment successful! Your account is ready — please log in.');
        const prefillEmail = sessionStorage.getItem('postPayEmail') || '';
        sessionStorage.removeItem('postPayEmail');
        setFormData((prev) => ({ ...prev, email: prefillEmail, password: '', name: '' }));
        setShowAuth(true);
        return;
      }
      if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), INTERVAL_MS);
    } catch (err) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), INTERVAL_MS);
    }
  };

  const handleSelectPackage = useCallback(() => {
    setShowRegister(true);
    // Notify admin about interest (fire-and-forget)
    axios.post(`${API}/track/interest`, { package: 'full', source: 'landing_page' }).catch(() => {});
  }, []);

  // Structured data for SEO
  const landingPageStructuredData = [
    generateFAQSchema(faqData),
    courseSchema
  ];

  // Hero bullet points
  const heroSections = [
    {
      title: 'A personalized study plan that evolves with your performance',
      bullets: [
        'An adaptive diagnostic identifies your strengths and skill gaps',
        'Your study path updates as you complete tests and practice',
        'Focus on the skills that improve your score.'
      ]
    },
    {
      title: 'Practice the skills that matter most',
      bullets: [
        '4,500+ SAT questions with clear, step-by-step solutions',
        'Targeted practice sets built around your key development areas',
        'Practice stays aligned with your evolving study plan'
      ]
    },
    {
      title: 'Verify your progress with official-style digital SAT tests',
      bullets: [
        'Full-length adaptive SAT tests with realistic module routing',
        'Calibrated difficulty and scoring for reliable score estimates.',
        'Clear analytics to measure score growth over time'
      ]
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <SEOHead
        title={seoConfigs.home.title}
        description={seoConfigs.home.description}
        canonical={seoConfigs.home.canonical}
        keywords={seoConfigs.home.keywords}
      />

      {/* Structured data inline for pre-rendering */}
      {landingPageStructuredData.map((sd, i) => (
        <script key={`sd-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(sd) }} />
      ))}

      <SignupBanner onSignUpClick={() => setShowTrialRequest(true)} />
      <FloatingTrialBubble />
      <Header variant="landing" onSignIn={() => setShowAuth(true)} />

      <main style={{ flex: 1 }}>
      {/* Hero Section */}
      <section aria-labelledby="hero-heading" style={{ padding: '1.5rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem', alignItems: 'center' }}>
          <div>
            <h1 id="hero-heading" className="color-text" style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', lineHeight: '1.2' }}>
              Improve Your SAT Score with a Personalized Study Plan
            </h1>
            <p style={{ color: '#666', marginBottom: '1rem', lineHeight: '1.5', fontSize: '1rem' }}>
              A smarter, structured path to higher SAT scores.
            </p>
            <div className="hero-content" style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
              {heroSections.map((section, sectionIdx) => (
                <div key={sectionIdx} style={{ marginBottom: '1rem' }}>
                  <p style={{ fontWeight: '700', color: '#0030b3', marginBottom: '0.5rem', fontSize: '1rem' }}>
                    {section.title}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, paddingLeft: '0.5rem' }}>
                    {section.bullets.map((item, idx) => (
                      <li key={`bullet-${sectionIdx}-${idx}`} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-start' }}>
                        <CheckCircle size={18} style={{ color: '#4a9b9e', flexShrink: 0, marginTop: '0.15rem' }} />
                        <span style={{ flex: 1, fontSize: '0.95rem', color: '#374151', lineHeight: '1.5' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          
          {/* CTA Box Container */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f0f8ff 0%, #e8f4ff 100%)',
            border: '1px solid #d1e3f5',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            {/* Quick Quiz - Get Your Projected SAT Score */}
            <div style={{ 
              background: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem', 
                color: '#111827',
                lineHeight: '1.4'
              }}>
                <span style={{ fontSize: '1.25rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>📊</span>
                <span style={{ verticalAlign: 'middle' }}>Get Your Projected SAT Score in 10 Minutes</span>
                <span style={{ 
                  background: '#10b981', 
                  color: 'white', 
                  fontSize: '0.7rem', 
                  padding: '0.2rem 0.5rem', 
                  borderRadius: '4px', 
                  fontWeight: '600',
                  marginLeft: '0.5rem',
                  verticalAlign: 'middle',
                  display: 'inline-block'
                }}>FREE</span>
              </h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem', lineHeight: '1.5' }}>
                A short 10-question quiz based on real test-style questions.
              </p>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: '0 0 1.25rem 0',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                <li style={{ marginBottom: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#10b981' }}>✓</span> 10 SAT-style questions (R&W and Math)
                </li>
                <li style={{ marginBottom: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#10b981' }}>✓</span> Covers key SAT topics
                </li>
                <li style={{ marginBottom: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#10b981' }}>✓</span> Instant projected score (400–1600)
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#10b981' }}>✓</span> Takes about 10–12 minutes
                </li>
              </ul>
              <button
                type="button"
                onClick={() => window.location.href = '/quick-quiz'}
                data-testid="check-score-btn"
                style={{ 
                  width: '100%',
                  background: '#2563eb',
                  color: 'white',
                  borderRadius: '6px',
                  fontWeight: '600',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  marginBottom: '0.5rem'
                }}
              >
                Start My Free SAT Quiz
              </button>
              <p style={{ 
                textAlign: 'center', 
                color: '#6b7280', 
                fontSize: '0.75rem', 
                margin: 0 
              }}>
                No credit card/sign up required
              </p>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <FeaturesSection />
      
      {/* Take a Look Inside - Video Section */}
      <section id="demo-video" aria-labelledby="demo-video-heading" style={{ 
        padding: '1.5rem 2rem', 
        background: '#f8f9fa'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 id="demo-video-heading" className="color-text" style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            marginBottom: '0.5rem'
          }}>
            Take a look inside
          </h2>
          <p className="regular-text" style={{ 
            color: '#666', 
            marginBottom: '1.5rem'
          }}>
            See how our platform helps you achieve your target SAT score
          </p>
          
          {/* Demo Video */}
          <div style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '56.25%',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              background: '#000'
          }}>
            <video
              controls
              preload="none"
              playsInline
              poster="/p4s_poster.jpg"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              data-testid="demo-video"
            >
              <source src="/p4s_demo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      <PricingSection onSelectPackage={handleSelectPackage} />
      <FAQSection />
      </main>

      <Footer />

      <TrialRequestDialog open={showTrialRequest} onOpenChange={setShowTrialRequest} />

      {/* Lazy-loaded Auth Modals (only load when user clicks) */}
      {showAuth && (
        <Suspense fallback={null}>
          <LazyLoginDialog
            open={showAuth}
            onOpenChange={setShowAuth}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleLogin}
            loading={loading}
            onForgotPassword={() => { setShowAuth(false); setShowForgotPassword(true); }}
          />
        </Suspense>
      )}

      {showForgotPassword && (
        <Suspense fallback={null}>
          <LazyForgotPasswordDialog
            open={showForgotPassword}
            onOpenChange={setShowForgotPassword}
            resetEmail={resetEmail}
            setResetEmail={setResetEmail}
            onSubmit={handleForgotPassword}
            loading={loading}
            onBackToLogin={() => { setShowForgotPassword(false); setShowAuth(true); }}
          />
        </Suspense>
      )}

      {showRegister && (
        <Suspense fallback={null}>
          <LazyRegisterDialog
            open={showRegister}
            onOpenChange={setShowRegister}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handlePayment}
            loading={loading}
          />
        </Suspense>
      )}
    </div>
  );
}
